import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import api from '../services/api';
import { cacheGet, cacheSet, cacheInvalidate } from '../services/apiCache';
import InstrumentForm from '../components/Instruments/InstrumentForm';
import ImportModal from '../components/Instruments/ImportModal';
import EditInstrumentModal from '../components/Instruments/EditInstrumentModal';

export default function AssetsPage() {
  const { user } = useAuth();
  const [instruments, setInstruments] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [showNewInstrument, setShowNewInstrument] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, tag }
  const [deleteReason, setDeleteReason] = useState('');

  const [filterTag, setFilterTag] = useState('');
  const [filterDescription, setFilterDescription] = useState('');
  const [filterSector, setFilterSector] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterCalibratable, setFilterCalibratable] = useState('');
  const [tabClassificacao, setTabClassificacao] = useState('todos'); // 'todos' | 'instrumento' | 'padrao'
  const [availableClasses, setAvailableClasses] = useState([]);

  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState('tag');
  const [sortOrder, setSortOrder] = useState('asc');

  const { can } = usePermissions();
  const isSupervisor = user?.role === 'supervisor';
  const showActions = can('ativos', 'edit');
  const colSpan = showActions ? 7 : 6;

  const hasTextFilter = !!(filterTag || filterDescription || filterSector);
  const hasAnyFilter = hasTextFilter || !!filterStatus || !!filterClass || filterCalibratable !== '';

  const fetchInstruments = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '5000' });
    if (filterTag) params.set('tag', filterTag);
    if (filterDescription) params.set('description', filterDescription);
    if (filterSector) params.set('sector', filterSector);
    if (filterStatus) params.set('operationalStatus', filterStatus);
    if (filterClass) params.set('instrumentClassId', filterClass);
    if (filterCalibratable !== '') params.set('calibratable', filterCalibratable);
    if (tabClassificacao !== 'todos') params.set('classificacao', tabClassificacao);

    if (!hasAnyFilter && tabClassificacao === 'todos') {
      const cached = cacheGet('/instruments');
      if (cached) { setInstruments(cached.dados); setTotalCount(cached.total); setLoading(false); return; }
    }

    try {
      const res = await api.get(`/instruments?${params.toString()}`);
      const dados = res.data.dados || res.data;
      const total = res.data.total ?? dados.length;
      setInstruments(dados);
      setTotalCount(total);
      if (!hasAnyFilter && tabClassificacao === 'todos') {
        cacheSet('/instruments', { dados, total }, 5 * 60_000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTag, filterDescription, filterSector, filterStatus, filterClass, filterCalibratable, tabClassificacao]);

  // Debounce apenas para campos de texto; selects disparam imediatamente
  useEffect(() => {
    if (!hasTextFilter) { fetchInstruments(); return; }
    const t = setTimeout(fetchInstruments, 350);
    return () => clearTimeout(t);
  }, [fetchInstruments, hasTextFilter]);

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    try {
      const res = await api.get('/instrument-classes');
      setAvailableClasses(res.data);
    } catch (err) { console.error(err); }
  };

  const handleInstrumentCreated = (newInstrument) => {
    cacheInvalidate('/instruments');
    setInstruments(prev => [newInstrument, ...prev]);
    setTotalCount(prev => prev + 1);
    setShowNewInstrument(false);
  };

  const handleImportComplete = () => { cacheInvalidate('/instruments'); fetchInstruments(); };

  const handleInstrumentUpdated = (updated) => {
    setInstruments(prev => prev.map(i => i._id === updated._id ? updated : i));
  };

  const handleDelete = (id, tag) => {
    setConfirmDelete({ id, tag });
    setDeleteReason('');
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/instruments/${confirmDelete.id}`, { data: { reason: deleteReason || 'Não informado' } });
      cacheInvalidate('/instruments');
      setInstruments(prev => prev.filter(i => i._id !== confirmDelete.id));
      setTotalCount(prev => prev - 1);
      setConfirmDelete(null);
    } catch (err) {
      console.error(err);
    }
  };

  const getFilteredInstruments = () => {
    return [...instruments].sort((a, b) => {
      const valA = (a[sortField] || '').toString().toLowerCase();
      const valB = (b[sortField] || '').toString().toLowerCase();
      return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  };

  const clearFilters = () => {
    setFilterTag(''); setFilterDescription(''); setFilterSector('');
    setFilterStatus(''); setFilterClass(''); setFilterCalibratable('');
  };

  if (loading) return <div className="p-4 text-dark-400">Carregando...</div>;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Ativos</h2>
          <p className="text-xs text-dark-400">
            {hasAnyFilter ? `${totalCount} resultado(s) encontrado(s)` : `${totalCount} ativos cadastrados`}
          </p>
        </div>
        <div className="flex gap-2">
          {can('ativos', 'edit') && (
            <>
              <button onClick={() => setShowNewInstrument(true)} className="px-3 py-1.5 bg-accent-blue-dark text-blue-100 rounded text-xs hover:bg-accent-blue">
                ➕ Novo
              </button>
              <button onClick={() => setShowImport(true)} className="px-3 py-1.5 bg-emerald-700 text-emerald-100 rounded text-xs hover:bg-emerald-600">
                📥 Importar
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs de classificação */}
      <div className="flex gap-1 mb-3">
        {[
          { key: 'todos', label: 'Todos' },
          { key: 'instrumento', label: 'Instrumentos' },
          { key: 'padrao', label: 'Padrões Internos' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTabClassificacao(t.key)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              tabClassificacao === t.key
                ? 'bg-blue-700 text-white'
                : 'bg-dark-800 text-dark-400 hover:text-white border border-dark-600'
            }`}
          >
            {t.label}
            {tabClassificacao === t.key && (
              <span className="ml-1.5 opacity-60">{totalCount}</span>
            )}
          </button>
        ))}
      </div>

      <button onClick={() => setShowFilters(!showFilters)} className="text-xs text-accent-blue hover:underline mb-2">
        {showFilters ? '🔽 Ocultar filtros' : '🔍 Filtros'}
      </button>
      {showFilters && (
        <div className="bg-dark-800 border border-dark-600 rounded-lg p-2 mb-2 grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <input placeholder="TAG..." value={filterTag} onChange={e => setFilterTag(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white" />
          <input placeholder="Descrição..." value={filterDescription} onChange={e => setFilterDescription(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white" />
          <input placeholder="Setor..." value={filterSector} onChange={e => setFilterSector(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white" />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white">
            <option value="">Todos os status</option>
            <option value="ativo">Ativo</option>
            <option value="desativado">Desativado</option>
            <option value="backup">Backup</option>
            <option value="manutencao">Manutenção</option>
          </select>
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white">
            <option value="">Todas as classes</option>
            {availableClasses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
          <select value={filterCalibratable} onChange={e => setFilterCalibratable(e.target.value)} className="bg-dark-700 border-dark-500 rounded px-2 py-1 text-white">
            <option value="">Calibrável (todos)</option>
            <option value="true">Sim</option>
            <option value="false">Não</option>
          </select>
          <button onClick={clearFilters} className="text-accent-red hover:underline">Limpar</button>
        </div>
      )}

      <div className="bg-dark-800 border border-dark-600 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-dark-700 text-dark-400 uppercase tracking-wider">
              <th className="cursor-pointer px-3 py-2 text-left" onClick={() => { setSortField('tag'); setSortOrder(v => v === 'asc' ? 'desc' : 'asc'); }}>
                TAG {sortField === 'tag' && (sortOrder === 'asc' ? '▲' : '▼')}
              </th>
              <th className="cursor-pointer px-3 py-2 text-left" onClick={() => { setSortField('description'); setSortOrder(v => v === 'asc' ? 'desc' : 'asc'); }}>
                Descrição
              </th>
              <th className="cursor-pointer px-3 py-2 text-left" onClick={() => { setSortField('sector'); setSortOrder(v => v === 'asc' ? 'desc' : 'asc'); }}>
                Setor
              </th>
              <th className="px-3 py-2 text-left">Classe</th>
              <th className="cursor-pointer px-3 py-2 text-left" onClick={() => { setSortField('operationalStatus'); setSortOrder(v => v === 'asc' ? 'desc' : 'asc'); }}>
                Status
              </th>
              <th className="px-3 py-2 text-left">Calibrável</th>
              {showActions && <th className="px-3 py-2 text-left">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {getFilteredInstruments().length === 0 ? (
              <tr><td colSpan={colSpan} className="text-center py-8 text-dark-400">Nenhum ativo encontrado</td></tr>
            ) : (
              getFilteredInstruments().map(instrument => (
                <tr key={instrument._id} className="border-t border-dark-600 hover:bg-dark-700">
                  <td className="px-3 py-1.5 font-mono text-white font-bold cursor-pointer hover:underline">{instrument.tag}</td>
                  <td className="px-3 py-1.5 text-dark-300">{instrument.description}</td>
                  <td className="px-3 py-1.5 text-dark-400">{instrument.sector || '-'}</td>
                  <td className="px-3 py-1.5 text-dark-400">{instrument.instrumentClass?.name || <span className="text-slate-600 italic text-[10px]">sem classe</span>}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      instrument.operationalStatus === 'ativo' ? 'bg-green-900 text-green-300' :
                      instrument.operationalStatus === 'desativado' ? 'bg-gray-700 text-gray-400' :
                      instrument.operationalStatus === 'backup' ? 'bg-blue-900 text-blue-300' :
                      'bg-yellow-900 text-yellow-300'
                    }`}>
                      {instrument.operationalStatus === 'manutencao' ? 'Manutenção' : (instrument.operationalStatus || 'ativo').charAt(0).toUpperCase() + (instrument.operationalStatus || 'ativo').slice(1)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    {(instrument.calibratable ?? true)
                      ? <span className="text-emerald-400 font-medium">Sim</span>
                      : <span className="text-slate-500">Não</span>}
                  </td>
                  {showActions && (
                    <td className="px-3 py-1.5">
                      <div className="flex gap-2">
                        <button onClick={() => setEditingInstrument(instrument)} className="text-accent-blue hover:text-blue-300">✏️</button>
                        {isSupervisor && (
                          <button onClick={() => handleDelete(instrument._id, instrument.tag)} className="text-accent-red hover:text-red-400">🗑️</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showNewInstrument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="max-w-2xl w-full mx-4">
            <InstrumentForm onInstrumentCreated={handleInstrumentCreated} onClose={() => setShowNewInstrument(false)} />
          </div>
        </div>
      )}

      {editingInstrument && (
        <EditInstrumentModal instrument={editingInstrument} onClose={() => setEditingInstrument(null)} onUpdated={handleInstrumentUpdated} />
      )}

      <ImportModal isOpen={showImport} onClose={() => setShowImport(false)} onImportComplete={handleImportComplete} upsertMode />

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="text-base font-semibold text-white">Excluir {confirmDelete.tag}?</h3>
            <p className="text-xs text-slate-400">Esta ação não pode ser desfeita.</p>
            <input
              placeholder="Motivo da exclusão (opcional)"
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500"
            />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
                Cancelar
              </button>
              <button onClick={confirmDeleteAction} className="px-4 py-2 text-xs bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}